import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import Skeleton from 'react-loading-skeleton';
import { useTheme } from 'styled-components';
import { BackArrow, Button, TopProgress } from '../../../../components';
import Dialog from '../../../../components/dialog';
import UniversalModal from '../../../../components/modal-alerts/universal-modal';
import { HeadLine1, BodyDefault } from '../../../../components/typography/styled';
import { billingService } from '../../../../api';
import { useUi } from '../../../../context/ui.context';
import { usePageTitle } from '../../../../context/navigationContext';
import {
  executeOrderAction,
  formatDateTime,
  formatOrderTotal,
  getApiErrorMessage,
  getDisplayName,
  getEntityId,
  getOrderActions,
  getOrderStatusLabel,
  OrderAction,
} from '../helpers';
import {
  ActionsWrapper,
  Container,
  DetailLabel,
  DetailRow,
  DetailsGrid,
  DetailValue,
  EmptyText,
  FraudTable,
  Header,
  ItemCard,
  ItemMeta,
  ItemTitle,
  OrderStatusText,
  Panel,
  PanelTitle,
  Section,
  TitleWrapper,
} from '../styles';

type FraudCategoryItem = {
  value: any;
  nullable?: boolean;
  boolean?: boolean;
  percent?: boolean;
};

const stringifyValue = (value: any) => {
  if (value === null || value === undefined || value === '') return 'n/a';
  if (typeof value === 'object') return JSON.stringify(value);
  return `${value}`;
};

const renderFraudValue = (item: any, t: any) => {
  const valueItem: FraudCategoryItem = item && typeof item === 'object' && 'value' in item
    ? item
    : { value: item };

  if (valueItem.boolean) return valueItem.value ? t('YES') : t('NO');
  if (valueItem.percent) return valueItem.value !== null && valueItem.value !== undefined ? `${valueItem.value}%` : 'n/a';
  if (valueItem.nullable) return stringifyValue(valueItem.value);
  return stringifyValue(valueItem.value);
};

const OrderPage: FC = () => {
  const { orderId } = useParams<{orderId: string}>();
  const navigate = useNavigate();
  const [t] = useTranslation();
  const theme:any = useTheme();
  const { viewAlert } = useUi();

  const [order, setOrder] = useState<any>();
  const [pageLoading, setPageLoading] = useState(true);
  const [pendingAction, setPendingAction] = useState<OrderAction>();
  const [actionLoading, setActionLoading] = useState(false);

  usePageTitle(order?.id ? `${t('ORDER')}: ${order.id}` : t('ORDER'));

  const getOrder = useCallback((options: { silent?: boolean } = {}) => {
    if (!orderId) return;
    if (!options.silent) {
      setPageLoading(true);
    }
    billingService.getOrder(orderId)
      .then((res:any) => {
        setOrder(res.data?.object || res.data);
      })
      .catch((error:any) => {
        viewAlert({
          severity: 'error',
          message: getApiErrorMessage(error, t('ERROR_OCCURRED')),
        });
        navigate('/orders');
      })
      .finally(() => {
        if (!options.silent) {
          setPageLoading(false);
        }
      });
  }, [orderId, navigate, t, viewAlert]);

  useEffect(() => {
    getOrder();
  }, [getOrder]);

  const closeAction = () => setPendingAction(undefined);

  const handleActionConfirm = async () => {
    if (!order || !pendingAction) return;
    setActionLoading(true);
    try {
      await executeOrderAction(pendingAction.id, order.id);
      viewAlert({
        severity: 'success',
        message: pendingAction.successMessage,
      });
      closeAction();
      if (pendingAction.id === 'delete') {
        navigate('/orders');
      } else {
        getOrder({ silent: true });
      }
    } catch (error:any) {
      viewAlert({
        severity: 'error',
        message: getApiErrorMessage(error, t('ERROR_OCCURRED')),
      });
    } finally {
      setActionLoading(false);
    }
  };

  const fraudCheck = useMemo(() => {
    const simple: Record<string, any> = {};
    const categories: Record<string, Record<string, any>> = {};

    Object.entries(order?.fraud_check_result || {}).forEach(([key, value]: [string, any]) => {
      if (value && typeof value === 'object' && !Array.isArray(value)) {
        categories[key] = value;
      } else {
        simple[key] = value;
      }
    });

    return { simple, categories };
  }, [order]);

  if (pageLoading) {
    return (
      <Container>
        <TopProgress loading={pageLoading} />
        <Skeleton
          baseColor={theme.palette[300]}
          highlightColor={theme.palette[20]}
          width={250}
          height={30}
          style={{ marginBottom: '12px' }}
        />
        <Skeleton
          baseColor={theme.palette[300]}
          highlightColor={theme.palette[20]}
          width='100%'
          height={220}
        />
      </Container>
    );
  }

  if (!order) {
    return (
      <Container>
        <BodyDefault>{t('ORDER_NOT_FOUND')}</BodyDefault>
      </Container>
    );
  }

  const actions = getOrderActions(order, t);
  const items = order.items || [];
  const hasFraudCheck = Boolean(order.fraud_check_result);
  const clientId = getEntityId(order.client);
  const userId = getEntityId(order.user);

  return (
    <Container>
      <BackArrow fallbackPath='/orders' />
      <Header>
        <TitleWrapper>
          <HeadLine1>{t('ORDER')} #{order.id}</HeadLine1>
          <BodyDefault>
            <OrderStatusText status={order.status}>
              {getOrderStatusLabel(order.status, t)}
            </OrderStatusText>
            {' / '}
            {order.invoice?.status || 'n/a'}
          </BodyDefault>
        </TitleWrapper>
        <ActionsWrapper>
          {actions.map((action) => (
            <Button
              key={action.id}
              title={action.title}
              icon={action.icon}
              variant={action.id === 'accept' ? 'primary' : 'stroke'}
              size='small'
              onClick={() => setPendingAction(action)}
            />
          ))}
        </ActionsWrapper>
      </Header>

      <DetailsGrid>
        <Panel>
          <PanelTitle>{t('TIMELINE')}</PanelTitle>
          <DetailRow>
            <DetailLabel>{t('ORDER_DATE')}</DetailLabel>
            <DetailValue>{formatDateTime(order.order_date)}</DetailValue>
          </DetailRow>
          <DetailRow>
            <DetailLabel>{t('ISSUE_DATE')}</DetailLabel>
            <DetailValue>{formatDateTime(order.invoice?.issue_date)}</DetailValue>
          </DetailRow>
          <DetailRow>
            <DetailLabel>{t('DUE_DATE')}</DetailLabel>
            <DetailValue>{formatDateTime(order.invoice?.due_date)}</DetailValue>
          </DetailRow>
        </Panel>

        <Panel>
          <PanelTitle>{t('CLIENT')}</PanelTitle>
          <DetailRow>
            <DetailLabel>{t('CLIENT')}</DetailLabel>
            <DetailValue>
              {clientId ? <a href={`/clients-users/clients/${clientId}`}>{getDisplayName(order.client)}</a> : getDisplayName(order.client)}
            </DetailValue>
          </DetailRow>
          <DetailRow>
            <DetailLabel>{t('USER')}</DetailLabel>
            <DetailValue>
              {userId ? <a href={`/clients-users/users/${userId}`}>{getDisplayName(order.user)}</a> : getDisplayName(order.user)}
            </DetailValue>
          </DetailRow>
          <DetailRow>
            <DetailLabel>{t('INVOICE_ID')}</DetailLabel>
            <DetailValue>
              {order.invoice ? <a href={`/billing/invoices/${order.invoice.id}`}>{order.invoice.id}</a> : 'n/a'}
            </DetailValue>
          </DetailRow>
        </Panel>

        <Panel>
          <PanelTitle>{t('SUMMARY')}</PanelTitle>
          <DetailRow>
            <DetailLabel>{t('TOTAL')}</DetailLabel>
            <DetailValue>{formatOrderTotal(order.total, order.currency)}</DetailValue>
          </DetailRow>
          <DetailRow>
            <DetailLabel>{t('STATUS')}</DetailLabel>
            <DetailValue>
              <OrderStatusText status={order.status}>{getOrderStatusLabel(order.status, t)}</OrderStatusText>
            </DetailValue>
          </DetailRow>
          <DetailRow>
            <DetailLabel>{t('PAYMENT_STATUS')}</DetailLabel>
            <DetailValue>{order.invoice?.status || 'n/a'}</DetailValue>
          </DetailRow>
        </Panel>
      </DetailsGrid>

      <Section>
        <PanelTitle>{t('ITEMS')}</PanelTitle>
        {items.length > 0 ? items.map((item:any) => (
          <ItemCard key={item.id || `${item.name}-${item.service}`}>
            <div>
              <ItemTitle>
                {getEntityId(item.service) ? (
                  <a href={`/billing/services/${getEntityId(item.service)}`}>
                    {item.name || 'n/a'}{item.description ? ` - ${item.description}` : ''}
                  </a>
                ) : (
                  <span>{item.name || 'n/a'}{item.description ? ` - ${item.description}` : ''}</span>
                )}
              </ItemTitle>
              {item.configurable_options?.map((option:any, index:number) => (
                <ItemMeta key={`${item.id}-option-${index}`}>{option.display || getDisplayName(option)}</ItemMeta>
              ))}
            </div>
            <ItemMeta>{item.cycle_display || 'n/a'}</ItemMeta>
          </ItemCard>
        )) : <EmptyText>{t('NO_ITEMS_FOUND')}</EmptyText>}
      </Section>

      {hasFraudCheck ? (
        <Section>
          <PanelTitle>{t('FRAUD_CHECK_RESULTS')}</PanelTitle>
          {Object.keys(fraudCheck.simple).length > 0 ? (
            <FraudTable>
              <tbody>
                {Object.entries(fraudCheck.simple).map(([key, value]) => (
                  <tr key={key}>
                    <td>{key}</td>
                    <td>{renderFraudValue(value, t)}</td>
                  </tr>
                ))}
              </tbody>
            </FraudTable>
          ) : null}
          {Object.entries(fraudCheck.categories).map(([category, values]) => (
            <div key={category} style={{marginTop: '16px'}}>
              <PanelTitle>{category}</PanelTitle>
              <FraudTable>
                <tbody>
                  {Object.entries(values).map(([key, value]) => (
                    <tr key={`${category}-${key}`}>
                      <td>{key}</td>
                      <td>{renderFraudValue(value, t)}</td>
                    </tr>
                  ))}
                </tbody>
              </FraudTable>
            </div>
          ))}
        </Section>
      ) : null}

      <Dialog
        open={Boolean(pendingAction)}
        active={Boolean(pendingAction)}
        noIframe
        handleClose={closeAction}
      >
        <UniversalModal
          closeModal={handleActionConfirm}
          closeModalCancel={closeAction}
          alertTitle={pendingAction?.title}
          alertSubTitle={pendingAction?.message}
          loading={actionLoading}
        />
      </Dialog>
    </Container>
  );
};

export default OrderPage;
